import './FeedSearchBox.css';
import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { getKeyHandler } from 't-lib-ui/structure';
import { Spinner } from 't-lib-ui/widgets';
import { formatCategory } from '../utils/formatting';
import { AppState } from '../context/general';
import { RestCatalogAutocomplete, RestUserFeed } from '../schema-copy/api-schema';
import { autocomplete, autocompleteFetch } from '../connector/autocomplete-engine';
import { normalizeForAutocomplete } from '../schema-copy/autocomplete-helper';
import { isUrlLike } from '../utils/url';
import { searchFollowedFeeds, feedDomain } from '../utils/followedFeedSearch';
import { useReadingOffline } from '../utils/useOfflineMode';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import FeedIcon from './FeedIcon';
import { CategoryIcon } from './CategoryIcons';
import CheckmarkCircleIcon from './CheckmarkCircleIcon';

// Suggestion limits: followed feeds; categories while there are feed suggestions, and without.
const MAX_FOLLOWED = 5;
// Offline, followed feeds are all there is to find, so show more of them.
const MAX_FOLLOWED_OFFLINE = 10;
const MAX_CATEGORIES_WITH_FEEDS = 2;
const MAX_CATEGORIES = 5;

// Top 10 TLDs (by usage) + blog
const TOP_TLDS = ['com', 'net', 'org', 'edu', 'gov', 'uk', 'de', 'cn', 'jp', 'fr', 'blog'];

const buttonKeyHandler = getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any;

interface AutocompleteItem {
  line1: string;
  line2?: string;
  type?: 'search';
  /** Shown in the left gutter (feed or category icon). */
  icon?: React.ReactNode;
  /** A followed feed: marked with FeedTile's "following" check. */
  following?: boolean;
  action: () => void;
}

interface FeedSearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  /** Search for the current input: a catalog search, or a feed lookup if it's URL-like. */
  onSubmit: () => void;
  onSelectCategory: (category: string) => void;
  onSelectFeed: (feedId: string) => void;
  /** All categories (incl. subcategories), for the category suggestions. */
  categories: string[];
  /** While true (a search has been performed for the input), no suggestions are shown. */
  searchActive?: boolean;
  /** Escape while no suggestions are open. */
  onEscape?: () => void;
  autoFocus?: boolean;
  /** Whether followed feeds are suggested first (also when the catalog doesn't suggest them). Off on
   *  Add Feed, which is about new feeds: there, followed feeds are only marked as such. Default: true. */
  preferFollowed?: boolean;
  placeholder?: string;
}

/** The search box of Add Feed (also on the Home page): website, topic or feed URL, with autocomplete
 *  suggestions for categories and catalog feeds and a full catalog search as last entry. */
const FeedSearchBox: React.FC<FeedSearchBoxProps> = ({ value, onChange, onSubmit, onSelectCategory, onSelectFeed, categories, searchActive, onEscape, autoFocus, preferFollowed = true, placeholder = 'Website or topic or RSS URL' }) => {
  const appState = useContext(AppState);
  // Offline only followed feeds can be found: no catalog requests, no URL / category / search entries.
  const offline = useReadingOffline();
  const inputRef = useRef<HTMLInputElement>(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [catalogAutocomplete, setCatalogAutocomplete] = useState<RestCatalogAutocomplete>([]);
  const [isLoadingAutocomplete, setIsLoadingAutocomplete] = useState(false);
  // Keyboard navigation scrolls the selected suggestion into view (hovering doesn't, it would jump).
  const listRef = useRef<HTMLDivElement>(null);
  const scrollToSelectedRef = useRef(false);
  useEffect(() => {
    if (!scrollToSelectedRef.current) return;
    scrollToSelectedRef.current = false;
    const selected = listRef.current?.querySelector('.selected');
    if (selected) selected.scrollIntoView({ block: 'nearest' });
    else listRef.current?.scrollTo({ top: 0 });
  }, [selectedIndex]);

  // Debounced autocomplete
  useEffect(() => {
    const trimmed = value.trim();

    // Only fetch autocomplete for non-URL inputs that have content
    if (!trimmed || isUrlLike(trimmed) || offline) {
      setCatalogAutocomplete([]);
      setIsLoadingAutocomplete(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        // Works for anonymous users too — the catalog autocomplete endpoint is public.
        const auth = appState.state.authData;
        const results = await autocomplete(auth, trimmed);

        if (results == 'loading') {
          // Need to fetch from network
          setIsLoadingAutocomplete(true);
          setCatalogAutocomplete(await autocompleteFetch(auth, normalizeForAutocomplete(trimmed)));
        } else
          setCatalogAutocomplete(results);
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        setCatalogAutocomplete([]);
      }
      setIsLoadingAutocomplete(false);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [value, appState.state.authData, offline]);

  const submit = () => {
    setShowAutocomplete(false);
    // Offline there's no search to submit to: open the best followed match instead.
    if (offline) return autocompleteItems[0]?.action();
    onSubmit();
  };

  // Suggestions, in order: followed feeds, the URL action, categories, catalog feeds, full search.
  const autocompleteItems = useMemo((): AutocompleteItem[] => {
    const trimmed = value.trim();
    const isHttpUrl = trimmed.startsWith('http://') || trimmed.startsWith('https://');
    const selectFeed = (feedId: string) => () => {
      setShowAutocomplete(false);
      onSelectFeed(feedId);
    };

    const userFeeds = appState.feedEngine.getFeeds();
    const userFeedsById = new Map(userFeeds.map(f => [f.id, f]));
    // Followed feeds are shown with their icon and the "following" check.
    const followedItem = (feed: RestUserFeed): AutocompleteItem => ({
      line1: feed.shortTitle || feed.title,
      line2: feedDomain(feed),
      icon: <FeedIcon src={getFeedIconUrl(feed, 48)} size={22} circular={feed.circularIcon} fallback={<span className="addFeedAutocompleteIconFallback" />} />,
      following: true,
      action: selectFeed(feed.id),
    });

    if (offline)
      return trimmed ? searchFollowedFeeds(userFeeds, trimmed, MAX_FOLLOWED_OFFLINE).map(followedItem) : [];

    // Preferred, followed feeds come first: matched locally (no request), plus any the catalog suggests.
    const followed = !preferFollowed || isHttpUrl ? [] : searchFollowedFeeds(userFeeds, trimmed, MAX_FOLLOWED);
    if (preferFollowed) {
      const followedIds = new Set(followed.map(f => f.id));
      for (const [feedId] of catalogAutocomplete) {
        const feed = userFeedsById.get(feedId);
        if (feed && !followedIds.has(feedId) && followed.length < MAX_FOLLOWED) {
          followed.push(feed);
          followedIds.add(feedId);
        }
      }
    }
    const catalogFeeds = preferFollowed ? catalogAutocomplete.filter(([feedId]) => !userFeedsById.has(feedId)) : catalogAutocomplete;

    const items: AutocompleteItem[] = followed.map(followedItem);
    // Whether the input already resolved to a URL "add" action; if so we skip
    // the catalog-search entry (there is nothing to search — it's a direct URL).
    let hasUrlAction = false;

    // Check if input starts with http:// or https://
    if (isHttpUrl) {
      hasUrlAction = true;
      items.push({ line1: trimmed, line2: 'Add website or feed', action: submit });
    }
    // Check if input is a complete hostname (2 or 3 parts)
    // Pattern: 2-part (domain.tld) or 3-part (subdomain.domain.tld or www.domain.tld)
    else if (trimmed.match(/^[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})?$/)) {
      const tld = (trimmed.match(/^[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+\.([a-zA-Z]{2,})$/) ?? trimmed.match(/^[a-zA-Z0-9-]+\.([a-zA-Z]{2,})$/))?.[1].toLowerCase();
      if (tld && TOP_TLDS.includes(tld)) {
        hasUrlAction = true;
        items.push({ line1: trimmed, line2: 'Add website', action: submit });
      }
    }
    // Check for category matches (if input doesn't look like a URL). Only a few while there are
    // (or may soon be) feed suggestions, so they don't push the feeds out of view.
    else if (trimmed) {
      const searchLower = trimmed.toLowerCase();
      const haveFeeds = followed.length || catalogFeeds.length || isLoadingAutocomplete;
      categories
        // The part after the last '/' (or the whole string) starts with the search term
        .filter(cat => cat.split('/').pop()!.toLowerCase().startsWith(searchLower))
        .slice(0, haveFeeds ? MAX_CATEGORIES_WITH_FEEDS : MAX_CATEGORIES)
        .forEach(cat => {
          const formatted = formatCategory(cat);
          if (formatted)
            items.push({
              line1: formatted.replace(/\//g, ' / '),
              line2: 'Category',
              icon: <CategoryIcon category={cat} size={20} />,
              action: () => {
                setShowAutocomplete(false);
                onSelectCategory(cat);
              }
            });
        });
    }

    // Then the catalog's feed suggestions
    catalogFeeds.forEach(([feedId, title, domain]) => {
      const feed = userFeedsById.get(feedId);
      items.push(feed ? followedItem(feed) : { line1: title, line2: domain, action: selectFeed(feedId) });
    });

    // Autocomplete only covers a subset of the catalog, so always offer a full
    // search as the last entry (unless the input is a direct URL).
    if (trimmed && !hasUrlAction)
      items.push({ line1: trimmed, type: 'search', action: submit });

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, categories, catalogAutocomplete, isLoadingAutocomplete, preferFollowed, offline]);

  useEffect(() => {
    if (!value.trim()) {
      setShowAutocomplete(false);
      return;
    }
    // Show autocomplete if there are suggestions or we're still loading,
    // but not once the user has performed a search for the input.
    if (!searchActive)
      setShowAutocomplete(autocompleteItems.length > 0 || isLoadingAutocomplete);
    // The first suggestion is preselected, so Return takes it. ArrowUp from there deselects it, and
    // Return then searches, like the Search button and the "Search for" entry.
    scrollToSelectedRef.current = true;
    setSelectedIndex(0);
  }, [value, autocompleteItems.length, isLoadingAutocomplete, searchActive]);

  const select = (index: number) => autocompleteItems[index]?.action();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showAutocomplete && autocompleteItems.length) {
      // Handle autocomplete navigation
      if (e.key == 'ArrowDown') {
        e.preventDefault();
        scrollToSelectedRef.current = true;
        setSelectedIndex(prev => prev < autocompleteItems.length - 1 ? prev + 1 : prev);
        return;
      }
      if (e.key == 'ArrowUp') {
        e.preventDefault();
        scrollToSelectedRef.current = true;
        setSelectedIndex(prev => prev > -1 ? prev - 1 : prev);
        return;
      }
      if (e.key == 'Enter') {
        e.preventDefault();
        if (selectedIndex < 0)
          submit();
        else
          select(selectedIndex);
        return;
      }
      if (e.key == 'Escape') {
        e.stopPropagation();
        setShowAutocomplete(false);
        return;
      }
    }
    if (e.key == 'Escape' && onEscape) {
      e.preventDefault();
      e.stopPropagation();
      onEscape();
      return;
    }

    // If not handling autocomplete, use FlowForm navigation: at the end of the text, ArrowRight moves on
    // to the clear button and the Search button (and ArrowLeft back).
    getKeyHandler({
      enterBehaviour: 'action',
      spaceBehaviour: 'ignore',
      sidewaysBehaviour: 'bothText',
      onAction: submit
    })(e as any);
  };

  const autocompleteOpen = showAutocomplete && autocompleteItems.length > 0;
  return (
    <div className={`addFeedSearchWrapper${autocompleteOpen ? ' withAutocomplete' : ''}`}>
      <svg className="addFeedSearchIcon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'var(--gray-500)' }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        ref={inputRef}
        type="text"
        enterKeyHint="search"
        placeholder={offline ? 'Search your feeds' : placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        // Close autocomplete when input loses focus (delayed so a click on a suggestion lands first)
        onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
        className="addFeedInput flowFocussable"
        tabIndex={0}
        autoFocus={autoFocus}
      />
      {value && (
        <button
          type="button"
          className="addFeedClearButton flowFocussable sidewayFocus"
          tabIndex={0}
          aria-label="Clear"
          title="Clear"
          onKeyDown={buttonKeyHandler}
          onClick={() => {
            onChange('');
            inputRef.current?.focus();
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </button>
      )}
      <button
        type="button"
        className="addFeedSearchButton flowFocussable sidewayFocus"
        tabIndex={0}
        onKeyDown={buttonKeyHandler}
        onClick={submit}
      >
        Search
      </button>
      {autocompleteOpen && (
        <div className="addFeedAutocomplete" ref={listRef}>
          {autocompleteItems.map((item, index) => item.type == 'search'
            ? (
              <React.Fragment key={index}>
                {isLoadingAutocomplete && (
                  <div className="addFeedAutocompleteItem addFeedAutocompleteLoading">
                    <Spinner spinning={true} />
                    <span>Loading more...</span>
                  </div>
                )}
                <div
                  className={`addFeedAutocompleteItem addFeedAutocompleteSearch ${index == selectedIndex ? 'selected' : ''}`}
                  onClick={() => select(index)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <svg className="addFeedAutocompleteSearchIcon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <span className="addFeedAutocompleteSearchLabel">Search for “{item.line1}”</span>
                </div>
              </React.Fragment>
            ) : (
              <div
                key={index}
                className={`addFeedAutocompleteItem ${index == selectedIndex ? 'selected' : ''}`}
                onClick={() => select(index)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {item.icon && <span className="addFeedAutocompleteIcon">{item.icon}</span>}
                <div className="addFeedAutocompleteText">
                  <div className="addFeedAutocompleteLine1">{item.line1}</div>
                  {item.line2 && <div className="addFeedAutocompleteLine2">{item.line2}</div>}
                </div>
                {item.following && (
                  <span className="addFeedAutocompleteFollowing" title="Following" aria-label="Following">
                    <CheckmarkCircleIcon size={17} color="currentColor" />
                  </span>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default FeedSearchBox;
